import { useUserPreferences } from "@/hooks/useUserPreferences";
import {
  ELECTRIC_RATE_UNITS,
  ELECTRIC_USAGE_UNITS,
  GAS_UNIT_CATALOG,
  formatRate,
  formatUsage,
} from "@/lib/units";

import type { ReactNode } from "react";

import { PreferenceCard } from "./PreferenceCard";
import { UNIT_ROW_GRID, UnitRow } from "./UnitRow";

/**
 * Sample values the previews draw, canonical (`$/kWh`, kWh, `$/therm`,
 * therms). Each carries more digits than any precision shows, so every
 * decimals pick visibly changes the preview.
 */
const SAMPLE_ELECTRIC_RATE = 0.073915;
const SAMPLE_ELECTRIC_USAGE = 1_234_567;
const SAMPLE_GAS_RATE = 0.512345;
const SAMPLE_GAS_USAGE = 123_456;

const GAS_RATE_UNITS = GAS_UNIT_CATALOG.map((u) => ({ code: u.code, label: u.rateLabel }));
const GAS_USAGE_UNITS = GAS_UNIT_CATALOG.map((u) => ({ code: u.code, label: u.usageLabel }));

/**
 * Preferences → Units: the unit and decimal places for electric and gas
 * rates and usage. These drive every rate and usage in the app — screens,
 * charts, exports and the documents a user generates — through `lib/units`.
 *
 * Changing a unit resets that row's decimals to the new unit's natural
 * precision (`$/kWh` 4, `$/MWh` 2, …), so a pick that suited one unit never
 * strands the next at `$0.07`; the decimals control then sets any 0–5.
 */
/** A commodity card's rows under one column header, on the rows' own grid. */
function UnitRows({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col">
      <div
        aria-hidden
        className={`${UNIT_ROW_GRID} hidden pb-2 text-xs font-medium text-muted-foreground sm:grid`}
      >
        <span />
        <span>Unit</span>
        <span>Decimals</span>
        <span className="text-right">Example</span>
      </div>
      <div className="flex flex-col divide-y divide-border">{children}</div>
    </div>
  );
}

export function UnitsSection() {
  const { effective, save, readOnly } = useUserPreferences();
  const units = effective.units;

  return (
    <div className="flex flex-col gap-4">
      <PreferenceCard title="Electric">
        <UnitRows>
          <UnitRow
            commodity="Electric"
            label="Rate"
            units={ELECTRIC_RATE_UNITS}
            unit={units.electricRate}
            onUnitChange={(u) => save({ electric_rate_unit: u, clear: ["electric_rate_precision"] })}
            precision={units.electricRatePrecision}
            onPrecisionChange={(p) => save({ electric_rate_precision: p })}
            preview={formatRate("electric", SAMPLE_ELECTRIC_RATE, units)}
            disabled={readOnly}
          />
          <UnitRow
            commodity="Electric"
            label="Usage"
            units={ELECTRIC_USAGE_UNITS}
            unit={units.electricUsage}
            onUnitChange={(u) => save({ electric_usage_unit: u, clear: ["electric_usage_precision"] })}
            precision={units.electricUsagePrecision}
            onPrecisionChange={(p) => save({ electric_usage_precision: p })}
            preview={formatUsage("electric", SAMPLE_ELECTRIC_USAGE, units)}
            disabled={readOnly}
          />
        </UnitRows>
      </PreferenceCard>

      <PreferenceCard title="Natural gas">
        <UnitRows>
          <UnitRow
            commodity="Natural gas"
            label="Rate"
            units={GAS_RATE_UNITS}
            unit={units.gasRate}
            onUnitChange={(u) => save({ gas_rate_unit: u, clear: ["gas_rate_precision"] })}
            precision={units.gasRatePrecision}
            onPrecisionChange={(p) => save({ gas_rate_precision: p })}
            preview={formatRate("natural_gas", SAMPLE_GAS_RATE, units)}
            disabled={readOnly}
          />
          <UnitRow
            commodity="Natural gas"
            label="Usage"
            units={GAS_USAGE_UNITS}
            unit={units.gasUsage}
            onUnitChange={(u) => save({ gas_usage_unit: u, clear: ["gas_usage_precision"] })}
            precision={units.gasUsagePrecision}
            onPrecisionChange={(p) => save({ gas_usage_precision: p })}
            preview={formatUsage("natural_gas", SAMPLE_GAS_USAGE, units)}
            disabled={readOnly}
          />
        </UnitRows>
      </PreferenceCard>
    </div>
  );
}
